import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import {ReactComponent as CloseIcon} from '../../assests/svg/close_icon.svg';
import AurIconButton from '../common/aur-button/AurIconButton';
import {t} from 'i18next';
import {isEmpty} from '../../utils/utils';
import {PermissionGroup} from '../../types/permission-group/permissionGroup';
import {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {Permission} from '../../types/permission-group/permission';
import {
  PERMISSION_NAME_MAP,
  PERMISSION_TYPE,
  PERMISSION_GROUP_CATEGORY,
} from '../../constants/constants';
import {RootState} from '../../redux/store';
import {
  FeatureSettingEnum,
  isFeatureSettingEnabled,
} from '../../redux/slices/featureSettingsSlice';

interface PermissionGroupModalProps {
  open: boolean;
  onClose: (e: React.MouseEvent<HTMLButtonElement>) => void;
  permissionModalData?: ItemType;
  selectedPermissionGroup?: PermissionGroup | null | undefined;
  permCategory: PERMISSION_GROUP_CATEGORY;
}

export interface ItemType {
  ItemKey: string;
  ItemName: string;
  ItemDescription: string;
  ItemFeatures: string[];
  ItemFeaturesForECI: string[];
  ItemFeaturesForBudget: string[];
  ItemEvidenceSourceManagement: string[];
  ItemFeaturesForSystem: string[];
  ItemFeaturesForManageProject: string[];
  ItemFeaturesForBusinessIntelligence: string[];
}

interface IPermissionGroupDetails {
  name: string;
  description: string;
  ECI: Array<string>;
  budget: Array<string>;
  reports: Array<string>;
  evidenceSourceManagement?: Array<string>;
  system: Array<string>;
  manageProject: Array<string>;
  businessIntelligence: Array<string>;
}

const PermissionGroupDialog: React.FC<PermissionGroupModalProps> = ({
  open,
  onClose,
  permissionModalData,
  selectedPermissionGroup,
  permCategory,
}) => {
  const [permissionGroupDetails, setPermissionGroupDetails] =
    useState<IPermissionGroupDetails>({
      name: '',
      description: '',
      ECI: [],
      budget: [],
      reports: [],
      evidenceSourceManagement: [],
      system: [],
      manageProject: [],
      businessIntelligence: [],
    });

  const isPowerBIReportsEnabled = useSelector((state: RootState) =>
    isFeatureSettingEnabled(state, FeatureSettingEnum.PowerBIReportsEnabled)
  );

  const separatePermissions = (permissions: Permission[], type: string) => {
    return permissions
      .filter((perm: Permission) => perm.type === type)
      .map((perm: Permission) => perm.permissionCode);
  };
  const localizePermissions = (permissions: string[]) => {
    return permissions?.map((name) => t(PERMISSION_NAME_MAP[name] || name));
  };

  useEffect(() => {
    if (permissionModalData) {
      setPermissionGroupDetails({
        name: permissionModalData.ItemName,
        description: permissionModalData.ItemDescription,
        reports: localizePermissions(permissionModalData.ItemFeatures),
        budget: localizePermissions(permissionModalData.ItemFeaturesForBudget),
        ECI: localizePermissions(permissionModalData.ItemFeaturesForECI),
        evidenceSourceManagement: localizePermissions(
          permissionModalData.ItemEvidenceSourceManagement
        ),
        system: localizePermissions(
          permissionModalData.ItemFeaturesForSystem ?? []
        ),
        manageProject: localizePermissions(
          permissionModalData.ItemFeaturesForManageProject ?? []
        ),
        businessIntelligence: localizePermissions(
          permissionModalData.ItemFeaturesForBusinessIntelligence ?? []
        ),
      });
      return;
    }

    if (selectedPermissionGroup) {
      const reports = separatePermissions(
        selectedPermissionGroup?.permissions,
        PERMISSION_TYPE.REPORTS
      );
      const budget = separatePermissions(
        selectedPermissionGroup?.permissions,
        PERMISSION_TYPE.BUDGET
      );
      const ECI = separatePermissions(
        selectedPermissionGroup?.permissions,
        PERMISSION_TYPE.ECI
      );

      const evidence = separatePermissions(
        selectedPermissionGroup?.permissions,
        PERMISSION_TYPE.EVIDENCE
      );

      const system = separatePermissions(
        selectedPermissionGroup?.permissions,
        PERMISSION_TYPE.SYSTEM
      );

      const manageProject = separatePermissions(
        selectedPermissionGroup?.permissions,
        PERMISSION_TYPE.MANAGE_PROJECT
      );
      const businessIntelligence = separatePermissions(
        selectedPermissionGroup?.permissions,
        PERMISSION_TYPE.BUSINESS_INTELLIGENCE
      );
      setPermissionGroupDetails({
        name: selectedPermissionGroup?.name,
        description: selectedPermissionGroup?.description,
        reports: localizePermissions(reports),
        budget: localizePermissions(budget),
        ECI: localizePermissions(ECI),
        evidenceSourceManagement: localizePermissions(evidence),
        system: localizePermissions(system),
        manageProject: localizePermissions(manageProject),
        businessIntelligence: localizePermissions(businessIntelligence),
      });
    }
  }, [selectedPermissionGroup, permissionModalData]);

  return (
    <Dialog
      open={open}
      maxWidth={false}
      PaperProps={{className: 'permission-group-dialog'}}
    >
      <AurIconButton
        className="grid-action-button align-self-end"
        icon={<CloseIcon aria-hidden="true" />}
        onClick={(e) => onClose(e)}
        ariaLabel={t('buttons.close')}
      />
      <Box>
        <DialogTitle className="dialog-title">
          <Typography className="typo-text headline3_size">
            {permissionGroupDetails.name}
          </Typography>
          <Typography className="typo-text body2_size">
            {permissionGroupDetails.description}
          </Typography>
        </DialogTitle>

        <DialogContent className="dialog-content">
          {!isEmpty(permissionGroupDetails?.system) && (
            <Box className="permission-gap">
              <Typography className="typo-text subhead4_size">
                {t('permissionGroupList.system')}
              </Typography>
              <Typography className="typo-text body3_size">
                {permissionGroupDetails?.system?.join(', ')}
              </Typography>
            </Box>
          )}
          {!isEmpty(permissionGroupDetails?.manageProject) && (
            <Box className="permission-gap">
              <Typography className="typo-text subhead4_size">
                {permCategory === PERMISSION_GROUP_CATEGORY.CLIENT
                  ? t('permissionGroupList.projectAdministration')
                  : t('permissionGroupList.manageProject')}
              </Typography>
              <Typography className="typo-text body3_size">
                {permissionGroupDetails?.manageProject
                  ?.slice()
                  .sort()
                  .join(', ')}
              </Typography>
            </Box>
          )}
          {!isEmpty(permissionGroupDetails.reports) && (
            <Box className="permission-gap">
              <Typography className="typo-text subhead4_size">
                {t('permissionGroupList.reporting')}
              </Typography>
              <Typography className="typo-text body3_size">
                {permissionGroupDetails.reports?.slice().sort().join(', ')}
              </Typography>
            </Box>
          )}
          {!isEmpty(permissionGroupDetails.businessIntelligence) &&
            isPowerBIReportsEnabled && (
              <Box className="permission-gap">
                <Typography className="typo-text subhead4_size">
                  {t('permissionGroupList.businessIntelligence')}
                </Typography>
                <Typography className="typo-text body3_size">
                  {permissionGroupDetails.businessIntelligence
                    ?.slice()
                    .sort()
                    .join(', ')}
                </Typography>
              </Box>
            )}
          {!isEmpty(permissionGroupDetails.ECI) && (
            <Box className="permission-gap">
              <Typography className="typo-text subhead4_size">
                {t('permissionGroupList.ECI')}
              </Typography>
              <Typography className="typo-text body3_size">
                {permissionGroupDetails.ECI?.join(', ')}
              </Typography>
            </Box>
          )}
          {!isEmpty(permissionGroupDetails.budget) && (
            <Box className="permission-gap">
              <Typography className="typo-text subhead4_size">
                {t('permissionGroupList.budget')}
              </Typography>
              <Typography className="typo-text body3_size">
                {permissionGroupDetails.budget?.join(', ')}
              </Typography>
            </Box>
          )}
          {!isEmpty(permissionGroupDetails.evidenceSourceManagement) && (
            <Box className="permission-gap">
              <Typography className="typo-text subhead4_size">
                {t('permissionGroupList.evidenceSourceManagement')}
              </Typography>
              <Typography className="typo-text body3_size">
                {permissionGroupDetails.evidenceSourceManagement?.join(', ')}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Box>
    </Dialog>
  );
};

export default PermissionGroupDialog;
